import {
  Bar,
  BarChart,
  Button,
  CartesianGrid,
  ChartContainer,
  ChartTooltip,
  Line,
  LineChart,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  XAxis,
  YAxis,
  cn,
} from '@flatfile/baseline'
import { getDashboardAnalytics } from '../../pages/CustomApps/hooks/getDashboardAnalytics'
import { formatDate } from '../../pages/CustomApps/utilities/customAppHelpers'

interface ChartTooltipProps {
  title: string
  desc?: string
  label: string
  payload: Record<string, any>[] | undefined
}

function CustomChartTooltip({ title, desc, label, payload }: ChartTooltipProps) {
  if (!payload) return null

  return (
    <div className='p-3 border shadow-md rounded-md bg-base-white'>
      <Text className='font-medium mb-1'>{formatDate(label).toLocaleString()}</Text>
      {payload.map((item: any) => (
        <Text key={item.name} className='text-sm' style={{ color: item.color }}>
          {title}: {Intl.NumberFormat('en-US').format(item.value)}
          {desc && `${desc}`}
        </Text>
      ))}
    </div>
  )
}

export enum ChartType {
  BAR,
  LINE,
}

const getMaxValue = (metrics: any[], property: keyof any) => {
  return metrics.reduce((max, item) => {
    if (item[property] > max) {
      return item[property]
    }
    return max
  }, metrics[0][property])
}

export const SimpleChart = ({ type, data, expanded }: { type: ChartType; data: any[]; expanded?: boolean }) => {
  const chartConfig = {
    metricValue: {
      label: 'Value',
      color: expanded ? 'hsl(var(--chart-1))' : 'hsl(var(--tokens-light-color-gray-3))',
    },
  }

  return (
    <>
      {type === ChartType.BAR ? (
        <div className='w-full relative'>
          <div className='absolute inset-0 -ml-16'>
            <ChartContainer config={chartConfig} className='h-full'>
              <BarChart data={data}>
                <Bar dataKey='metricValue' fill='var(--color-metricValue)' />
                <XAxis dataKey='dimensionValue' hide />
                <YAxis hide />
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      ) : (
        <ChartContainer config={chartConfig} className='h-[60px]'>
          <LineChart data={data}>
            <Line type='natural' dataKey='metricValue' stroke='var(--color-metricValue)' dot={false} strokeWidth={2} />
            <XAxis dataKey='dimensionValue' hide padding={{ left: -5, right: -5 }} />
            <YAxis hide padding={{ top: 6, bottom: 6 }} />
          </LineChart>
        </ChartContainer>
      )}
    </>
  )
}

export const FullChart = ({
  type,
  data,
  title,
  desc,
}: {
  type: ChartType
  data: any[]
  title: string
  desc?: string
}) => {
  const chartConfig = {
    metricValue: {
      label: title,
      color: 'hsl(var(--chart-1))',
    },
  }

  return (
    <div>
      {type === ChartType.BAR ? (
        <ChartContainer config={chartConfig} className='h-full -ml-16'>
          <BarChart data={data}>
            <Bar dataKey='metricValue' fill='var(--color-metricValue)' />
            <XAxis dataKey='dimensionValue' hide />
            <YAxis hide />
          </BarChart>
        </ChartContainer>
      ) : (
        <ChartContainer config={chartConfig} className='h-[290px] bg-[#FBFCFF]'>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='dimensionValue' tickMargin={0} orientation='bottom' />
            <YAxis tickMargin={15} orientation='right' padding={{ top: 30, bottom: 30 }} />
            <Line
              type='natural'
              dataKey='metricValue'
              stroke='var(--color-metricValue)'
              dot={{ r: 2 }}
              strokeWidth={2}
              connectNulls
            />
            <ChartTooltip
              content={(props) => {
                if (!props.payload || !props.payload.length || !props.label) return null
                return <CustomChartTooltip title={title} desc={desc} label={props.label} payload={props.payload} />
              }}
            />
          </LineChart>
        </ChartContainer>
      )}
    </div>
  )
}

export function MetricChart({
  name,
  tooltip,
  formatter,
  chartType,
  title,
  currentApp,
  environment,
  startDate,
  endDate,
  metrics,
  simplified,
  onClickViewMore,
  desc,
  expanded,
}: {
  name?: string
  tooltip?: string
  formatter?: (value: number) => string
  chartType: ChartType
  title: string
  currentApp: any
  environment: any
  startDate: string
  endDate: string
  metrics: string
  simplified?: boolean
  onClickViewMore?: () => void
  desc?: string
  expanded?: boolean
}) {
  const { analyticData: data, total } = getDashboardAnalytics(
    currentApp.id,
    environment.id,
    startDate,
    endDate,
    metrics
  )

  return (
    <div
      className={cn(
        'border rounded-none h-full',
        !simplified ? 'p-2 bg-[#FBFCFF] border-[#FBFCFF]' : 'p-4',
        simplified && expanded ? 'bg-white' : 'bg-[#FBFCFF]'
      )}
    >
      <div className='flex justify-between min-h-[25px]'>
        <div className='flex gap-1 items-center'>
          <Text
            className={cn(
              !simplified ? 'text-sm font-bold' : 'text-xs',
              !expanded && simplified ? 'text-text-default' : 'text-primary'
            )}
          >
            {!simplified ? name : title} {!simplified && 'over time'}
          </Text>
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                size='sm'
                variant='ghost'
                className='p-0'
                icon='circle-info'
                iconProps={{ className: expanded ? 'text-primary' : 'text-text-default', size: 16 }}
              />
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1 max-w-[300px]'>
              <Text variant='body' className='text-sm'>
                {tooltip}
              </Text>
            </TooltipContent>
          </Tooltip>
        </div>

        {simplified && (
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                size='icon-sm'
                variant='ghost'
                onClick={onClickViewMore}
                icon={!expanded ? 'expand-simple' : 'checkmark-2'}
                iconProps={{ size: 16 }}
              />
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1 max-w-[300px]'>
              <Text variant='body' className='text-sm'>
                {!expanded ? 'Expand Chart' : 'Currently Viewing'}
              </Text>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {simplified && total > 0 && (
        <div className={cn('flex gap-1 mt-1 min-h-[25px]', !expanded ? 'text-text-default' : 'text-primary')}>
          <Text>{formatter ? formatter(total) : total.toLocaleString()}</Text>
          {desc && <Text className='text-xs mt-[7px]'>{desc}</Text>}
        </div>
      )}

      {Object.keys(data).length === 0 || getMaxValue(data, 'metricValue') === 0 ? (
        <Text className='text-sm text-text-default pt-3 pb-6'>No data available for this time period</Text>
      ) : (
        <>
          {simplified ? (
            <SimpleChart type={chartType} data={data} expanded={expanded} />
          ) : (
            <FullChart type={chartType} data={data} title={title} desc={desc} />
          )}
        </>
      )}
    </div>
  )
}
